import { useState, useEffect } from "react"
import { FileText, Download, Send, Upload, Clock, AlertCircle, Loader2 } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { ReportStats } from "../components/ui/school/report-center/report-stats"
import { ReportTable } from "../components/ui/school/report-center/report-table"
import StudentDetailsModal from "../components/ui/school/report-center/student-details-modal"
import { UploadResultsModal } from "../components/ui/school/report-center/upload-results-modal"
import { DeleteConfirmModal } from "../components/ui/school/report-center/delete-confirm-modal"
import { UploadBulkResultsModal } from "../components/ui/school/report-center/upload-bulk-results-modal"
import { listStudents, exportStudentsCSV, uploadResults } from "@/hooks/api/schoolApi"

const TABS = ["All Reports", "Pending Verification"]

// Expected CSV columns for bulk result upload:
// studentEmail, examType, subject, score, grade
// e.g: john@school.com,WAEC,Mathematics,78,B2
const parseBulkResultsCSV = (text) => {
  const lines = text.split("\n").filter(Boolean)
  if (lines.length < 2) return []
  const headers = lines[0].split(",").map(h => h.trim().toLowerCase())
  return lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.trim())
    const row = Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? ""]))
    return {
      studentEmail: row.studentemail ?? row.email ?? "",
      examType: row.examtype ?? row.exam_type ?? row.type ?? "school_report",
      subject: row.subject ?? "",
      score: parseFloat(row.score ?? "0") || 0,
      grade: row.grade ?? "",
    }
  }).filter(r => r.studentEmail && r.subject)
}

const ReportCenterPage = () => {
  const [activeTab, setActiveTab] = useState("All Reports")
  const [students, setStudents] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false)
  const [uploadStudentId, setUploadStudentId] = useState("")
  const [deleteStudentId, setDeleteStudentId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const studentsPerPage = 10

  const mapStudent = (s) => ({
    id: s._id ?? s.id,
    name: `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "Student",
    studentId: s.studentId ?? s._id,
    class: s.classLevel ?? "—",
    gpa: String(s.performanceMetrics?.academicScore ?? s.currentGPA ?? "N/A"),
    status: s.status ?? "active",
    email: s.email ?? "",
    academicResults: s.academicResults ?? [],
  })

  const load = (page = currentPage) => {
    setLoading(true)
    setError(null)
    listStudents({ page, limit: studentsPerPage })
      .then(res => {
        const items = res.students ?? res ?? []
        setStudents(items.map(mapStudent))
        setTotal(res.total ?? items.length)
      })
      .catch(err => setError(err?.response?.data?.error?.message ?? "Could not load students."))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [currentPage])

  const pendingVerification = students.filter(s =>
    Array.isArray(s.academicResults) && s.academicResults.some(r => r.status === "pending")
  )

  const displayedStudents = activeTab === "Pending Verification" ? pendingVerification : students

  const totalPages = Math.ceil((activeTab === "Pending Verification" ? pendingVerification.length : total) / studentsPerPage)
  const startIndex = (currentPage - 1) * studentsPerPage
  const currentStudents = activeTab === "Pending Verification"
    ? pendingVerification.slice(startIndex, startIndex + studentsPerPage)
    : displayedStudents

  const avgGrade = students.length
    ? (students.reduce((sum, s) => sum + (parseFloat(s.gpa) || 0), 0) / students.length).toFixed(1)
    : "0"

  const handleSelectAll = (checked) => {
    if (checked) setSelectedIds(currentStudents.map(s => s.id))
    else setSelectedIds([])
  }

  const handleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id])
  }

  const handleViewDetails = (student) => {
    setSelectedStudent(student)
    setIsDetailsModalOpen(true)
  }

  const handleUpload = (id) => {
    const student = students.find(s => s.id === id)
    if (student) {
      setUploadStudentId(id)
      setSelectedStudent(student)
      setIsUploadModalOpen(true)
    }
  }

  const handleDeleteClick = (id) => {
    setDeleteStudentId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    setStudents(students.filter(s => s.id !== deleteStudentId))
    setSelectedIds(selectedIds.filter(id => id !== deleteStudentId))
    setIsDeleteModalOpen(false)
    setDeleteStudentId("")
  }

  const handleExportReports = async () => {
    try {
      const blob = await exportStudentsCSV()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `students-report-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert("Export failed. Please try again.")
    }
  }

  const handleSendToParents = () => {
    if (selectedIds.length === 0) return
    alert(`Sending reports to ${selectedIds.length} parent(s) via email…`)
    setSelectedIds([])
  }

  const handleBulkUpload = async (file) => {
    try {
      const text = await file.text()
      const results = parseBulkResultsCSV(text)
      if (!results.length) {
        alert("No valid rows found. CSV must have columns: studentEmail, examType, subject, score, grade")
        return
      }
      const res = await uploadResults(results)
      const processed = res?.updated ?? res?.processed ?? results.length
      alert(`Uploaded ${processed} result(s) successfully.`)
      load(1)
    } catch (err) {
      const msg = err?.response?.data?.error?.message ?? "Upload failed. Check the file format and try again."
      alert(msg)
    }
  }

  const studentToDelete = students.find(s => s.id === deleteStudentId)
  const uploadStudent = students.find(s => s.id === uploadStudentId)

  return (
    <SchoolLayout>
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-secondary mb-2">Report Center</h1>
            <p className="text-muted-foreground">
              Manage student academic reports, upload results, and send reports to parents.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setIsBulkUploadModalOpen(true)}
              variant="outline"
              className="border-secondary text-secondary hover:bg-secondary/10 bg-transparent"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Results
            </Button>
            <Button
              onClick={handleSendToParents}
              variant="outline"
              disabled={selectedIds.length === 0}
              className="border-primary text-primary hover:bg-primary/10 bg-transparent"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Parents ({selectedIds.length})
            </Button>
            <Button onClick={handleExportReports} className="bg-secondary hover:bg-secondary/90 text-white">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <ReportStats totalReports={total || students.length} reportsSent={Math.floor((total || students.length) * 0.75)} avgGrade={avgGrade} />

        {/* Tabs */}
        <div className="flex gap-1 mt-6 mb-4 bg-slate-100 p-1 rounded-xl w-fit">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setCurrentPage(1) }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === tab ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab === "Pending Verification" && (
                <Clock size={13} className={activeTab === tab ? "text-amber-500" : "text-slate-400"} />
              )}
              {tab}
              {tab === "Pending Verification" && pendingVerification.length > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {pendingVerification.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading students…</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 max-w-lg">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-16 space-y-2">
            <FileText className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="font-bold text-slate-700">No student records yet</p>
            <p className="text-slate-400 text-sm">Add students in the Data Center to see their reports here.</p>
          </div>
        ) : (
          <ReportTable
            students={currentStudents}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelect={handleSelect}
            onViewDetails={handleViewDetails}
            onUpload={handleUpload}
            onDelete={handleDeleteClick}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <StudentDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => { setIsDetailsModalOpen(false); setSelectedStudent(null) }}
        student={selectedStudent}
      />

      <UploadResultsModal
        isOpen={isUploadModalOpen}
        onClose={() => { setIsUploadModalOpen(false); setUploadStudentId(""); setSelectedStudent(null) }}
        studentId={uploadStudentId}
        studentName={uploadStudent?.name || ""}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteStudentId("") }}
        onConfirm={handleConfirmDelete}
        studentName={studentToDelete?.name || ""}
      />

      <UploadBulkResultsModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onUpload={handleBulkUpload}
      />
    </SchoolLayout>
  )
}

export default ReportCenterPage
